import Phaser from 'phaser';
import { AudioSystem } from '../systems/AudioSystem';
import { SaveSystem } from '../systems/SaveSystem';
import { hideEmergencyBagOverlay } from '../ui/EmergencyBagOverlay';
import { resetZoneHudVisibility } from '../ui/ZoneHudHeader';

const TRANSITION_DELAY_MS = 32;
const RESTART_WATCHDOG_MS = 1200;
const TRANSITION_WATCHDOG_MS = 800;
const SYSTEM_SCENES = new Set(['BootScene', 'PreloadScene']);

/** Transisi scene — tanpa layar hitam paksa agar resume/tampilan zona tidak blank. */
export class SceneTransition {
  /** Pastikan kamera bersih saat zona siap ditampilkan. */
  static reveal(scene: Phaser.Scene): void {
    if (scene.scene.isPaused()) {
      scene.scene.resume();
    }
    const cam = scene.cameras.main;
    cam.resetFX();
    cam.setAlpha(1);
    cam.setVisible(true);
    SceneTransition.enableInput(scene);
    resetZoneHudVisibility();
    SaveSystem.clearZoneRestartPending();
  }

  /** Fade-in singkat dari hitam setelah transisi antar-zona. */
  private static fadeInTarget(scene: Phaser.Scene, durationMs: number): void {
    const cam = scene.cameras.main;
    cam.resetFX();
    cam.setAlpha(1);
    cam.setVisible(true);
    cam.fadeIn(Math.max(120, Math.min(350, durationMs)), 0, 0, 0);
  }

  /** Pastikan input aktif setelah restart/transisi (hindari freeze). */
  static enableInput(scene: Phaser.Scene): void {
    if (!scene.input) return;
    scene.input.enabled = true;
    if (scene.input.keyboard) {
      scene.input.keyboard.enabled = true;
    }
  }

  /**
   * Lepas pointer yang masih menempel — jangan matikan input global.
   * Mematikan scene.input.enabled sebelum restart membuat zona freeze permanen.
   */
  private static resetPointers(scene: Phaser.Scene): void {
    if (!scene.input) return;
    try {
      scene.input.resetPointers();
    } catch {
      // ignore
    }
  }

  /** Transisi antar scene: reset pointer, input dimatikan sementara di scene lama. */
  private static prepareForTransition(scene: Phaser.Scene): void {
    if (!scene.input) return;
    SceneTransition.resetPointers(scene);
    scene.input.enabled = false;
  }

  /** Hentikan scene aktif yang nyangkut dari transisi sebelumnya. */
  private static stopStrayScenes(
    game: Phaser.Game,
    fromKey: string,
    targetKey: string,
  ): void {
    game.scene.getScenes(true).forEach((s) => {
      const key = s.scene.key;
      if (key === fromKey || key === targetKey || SYSTEM_SCENES.has(key)) return;
      if (s.scene.isActive()) {
        game.scene.stop(key);
      }
    });
  }

  /** Panggil reveal pada scene tujuan — beberapa percobaan agar tidak ketinggalan timing Phaser. */
  private static revealTargetWhenReady(
    game: Phaser.Game,
    targetScene: string,
    fadeInMs = 0,
  ): void {
    let revealed = false;
    let fadeStarted = false;

    const tryReveal = () => {
      const next = game.scene.getScene(targetScene);
      if (!next?.scene.isActive()) return;

      if (!revealed) {
        revealed = true;
        SceneTransition.reveal(next);
      }

      if (fadeInMs > 0 && !fadeStarted) {
        fadeStarted = true;
        SceneTransition.fadeInTarget(next, fadeInMs);
      }
    };

    const existing = game.scene.getScene(targetScene);
    existing?.events.once(Phaser.Scenes.Events.CREATE, tryReveal);
    existing?.events.once(Phaser.Scenes.Events.START, tryReveal);

    tryReveal();
    window.requestAnimationFrame(tryReveal);
    window.requestAnimationFrame(() => window.requestAnimationFrame(tryReveal));
    window.setTimeout(tryReveal, 50);
    window.setTimeout(tryReveal, 200);
    window.setTimeout(tryReveal, TRANSITION_WATCHDOG_MS);
  }

  /**
   * Mulai scene setelah pipeline input selesai.
   * WAJIB pakai ScenePlugin scene pemanggil (scene.scene.start) — game.scene.start
   * tidak menghentikan scene saat ini sehingga banyak scene jalan bersamaan & error.
   */
  static startNextFrame(
    scene: Phaser.Scene,
    targetScene: string,
    data?: object,
  ): void {
    const scenePlugin = scene?.scene;
    const game = scene?.game;
    if (!scenePlugin || !game) return;
    if (scenePlugin.key === targetScene) return;

    const fromKey = scenePlugin.key;
    SaveSystem.clearZoneRestartPending();
    hideEmergencyBagOverlay();
    AudioSystem.playSfx('TRANSITION');

    window.setTimeout(() => {
      try {
        SceneTransition.prepareForTransition(scene);
        SceneTransition.stopStrayScenes(game, fromKey, targetScene);
        scenePlugin.start(targetScene, data);
        const target = game.scene.getScene(targetScene);
        if (target?.scene.isActive()) {
          SceneTransition.reveal(target);
        }
        SceneTransition.revealTargetWhenReady(game, targetScene);
      } catch {
        const fromScene = game.scene.getScene(fromKey);
        if (fromScene?.scene.isActive()) {
          SceneTransition.reveal(fromScene);
        } else {
          game.scene.start(targetScene);
          SceneTransition.revealTargetWhenReady(game, targetScene);
        }
      }
    }, TRANSITION_DELAY_MS);
  }

  static fadeOutAndStart(
    scene: Phaser.Scene,
    targetScene: string,
    durationMs = 400,
    data?: object,
  ): void {
    const scenePlugin = scene?.scene;
    const game = scene?.game;
    if (!scenePlugin || !game) return;
    if (scenePlugin.key === targetScene) return;

    const fromKey = scenePlugin.key;
    SaveSystem.clearZoneRestartPending();
    hideEmergencyBagOverlay();
    AudioSystem.playSfx('TRANSITION');

    const cam = scene.cameras.main;
    cam.resetFX();
    cam.setAlpha(1);
    cam.setVisible(true);

    let launched = false;
    const launchTarget = () => {
      if (launched) return;
      launched = true;

      SceneTransition.prepareForTransition(scene);
      SceneTransition.stopStrayScenes(game, fromKey, targetScene);
      scenePlugin.start(targetScene, data);
      const target = game.scene.getScene(targetScene);
      if (target?.scene.isActive()) {
        SceneTransition.reveal(target);
      }
      SceneTransition.revealTargetWhenReady(game, targetScene, 0);
    };

    cam.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, launchTarget);
    cam.fadeOut(durationMs, 0, 0, 0);
    window.setTimeout(launchTarget, durationMs + 280);
  }

  /** Ulangi zona saat ini — reset save, stop+start scene, pastikan input hidup lagi. */
  static restartCurrentZone(scene: Phaser.Scene): void {
    if (SaveSystem.isZoneRestartPending()) return;

    const scenePlugin = scene?.scene;
    const game = scene?.game;
    if (!scenePlugin || !game) return;

    const key = scenePlugin.key;
    const zone = SaveSystem.getZoneForScene(key);

    hideEmergencyBagOverlay();
    AudioSystem.stopSpeech();
    SaveSystem.restartZone(zone, key);

    SceneTransition.resetPointers(scene);
    AudioSystem.playSfx('TRANSITION');

    window.setTimeout(() => {
      scenePlugin.stop();
      scenePlugin.start(key);
      SceneTransition.revealTargetWhenReady(game, key);
    }, TRANSITION_DELAY_MS);

    window.setTimeout(() => {
      if (!SaveSystem.isZoneRestartPending()) return;
      const active = game.scene.getScene(key);
      if (active?.scene.isActive()) {
        SceneTransition.reveal(active);
      } else {
        SaveSystem.clearZoneRestartPending();
      }
    }, RESTART_WATCHDOG_MS);
  }

  /** @deprecated Pakai restartCurrentZone */
  static restartNextFrame(scene: Phaser.Scene, data?: object): void {
    void data;
    SceneTransition.restartCurrentZone(scene);
  }
}
